import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { TicketSummary } from '@aurora/contracts';
import { patch, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useSession } from '../../lib/session';
import { Badge, Spinner } from '../../components/ui';
import { useToast } from '../../components/toast';
import { ticketStatusTone } from './status-tone';
import { invalidateTicket, patchTicketInLists } from './list-cache';

/**
 * Changing a ticket's status from the list, without opening it.
 *
 * Triage is the most repetitive thing an agent does — twenty tickets, each
 * needing one field changed — and making each one a page load, a scroll, a
 * click and a back button is what makes a queue feel like work.
 *
 * The new status shows at once and stays shown. It used to be cleared the
 * moment the request returned, before the refetched list arrived, so the badge
 * read "Resolved", flipped back to "Open", then read "Resolved" again — and in
 * the old status's colour throughout, because the colour ignored the choice.
 * The server's copy of the ticket now goes straight into the cached list, and
 * the colour follows whatever is being shown.
 *
 * It goes through the ordinary update endpoint: SLA recalculation, automation
 * and audit behave exactly as they do when the change is made in the ticket.
 * When the row knows which version of the ticket it was drawn from, that goes
 * too, so a change made from a list loaded a minute ago cannot silently undo
 * what somebody else did in the meantime.
 */

export interface StatusChoice {
  value: string;
  label: string;
  category?: string;
}

/** The version a row was drawn from. Optional in the contract, so checked rather than assumed. */
function versionOf(ticket: TicketSummary): number | undefined {
  return typeof ticket.version === 'number' ? ticket.version : undefined;
}

export function InlineStatus({
  ticket,
  choices,
  open,
  onOpenChange,
}: {
  ticket: TicketSummary;
  choices: StatusChoice[];
  /**
   * Whether the menu is open, for a list that opens it from the keyboard (the
   * row's `s` shortcut). Left out, the menu keeps its own state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const [optimistic, setOptimistic] = useState<StatusChoice | null>(null);

  // Once the row itself carries the new status, the stand-in has done its job.
  useEffect(() => {
    if (optimistic && ticket.status.key === optimistic.value) setOptimistic(null);
  }, [optimistic, ticket.status.key]);

  const change = useMutation<{ data?: TicketSummary }, ApiError, StatusChoice>({
    mutationFn: (choice) => {
      const version = versionOf(ticket);
      return patch(`/tickets/${ticket.number}`, { status: choice.value, ...(version !== undefined ? { version } : {}) });
    },
    onMutate: (choice) => setOptimistic(choice),
    onSuccess: async (response) => {
      if (response?.data) {
        // The row now holds the server's own copy — including a status an
        // automation may have changed again — so the stand-in can go in the
        // same render, with nothing in between to flicker back to.
        patchTicketInLists(queryClient, response.data);
        setOptimistic(null);
        invalidateTicket(queryClient, ticket.number);
        return;
      }
      // No copy of the ticket came back; wait for the refetch instead of guessing.
      invalidateTicket(queryClient, ticket.number);
      await queryClient.refetchQueries({ queryKey: ['tickets'], type: 'active' });
      setOptimistic(null);
    },
    onError: (failure) => {
      setOptimistic(null);
      // The server's own sentence: "Resolution notes" is needed before this
      // ticket can be resolved — not a generic failure. A stale write means the
      // row is out of date, so it is refreshed for the next attempt.
      toast.error(failure);
      if (failure.code === 'stale_write') invalidateTicket(queryClient, ticket.number);
    },
  });

  const label = optimistic?.label ?? ticket.status.label;
  const tone = ticketStatusTone(optimistic?.category ?? ticket.status.category);

  // Without edit rights this is exactly what it was before: a label.
  if (!can('ticket.edit') || choices.length === 0) {
    return (
      <Badge tone={tone} dot className="max-w-full" title={ticket.status.label}>
        <span className="truncate">{ticket.status.label}</span>
      </Badge>
    );
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          // Busy rather than disabled: a disabled button drops keyboard focus,
          // and somebody triaging by keyboard would lose their place.
          aria-busy={change.isPending || undefined}
          aria-label={`Status: ${label}. Change it.`}
          // The whole name, for a narrow column that has to cut it short.
          title={label}
          className={cn(
            'group/status inline-flex max-w-full min-w-0 items-center gap-0.5 rounded transition-opacity',
            change.isPending && 'opacity-70',
          )}
        >
          <Badge tone={tone} dot className="max-w-full min-w-0">
            <span className="truncate">{label}</span>
          </Badge>
          {change.isPending ? (
            <Spinner className="size-2.5" label="Saving" />
          ) : (
            // Faint at rest and full on hover or keyboard focus, so it reads
            // as a menu without shouting on every row.
            <ChevronDown
              aria-hidden
              className="size-3 shrink-0 text-ink-faint opacity-40 transition-opacity group-hover/status:opacity-100 group-focus-visible/status:opacity-100"
            />
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="animate-pop scrollbar-slim z-50 max-h-72 min-w-44 overflow-y-auto rounded-card border border-line bg-raised p-1 shadow-float"
        >
          {choices.map((choice) => {
            // By key, not label: two statuses may share a name.
            const current = choice.value === (optimistic?.value ?? ticket.status.key);
            return (
              <DropdownMenu.Item
                key={choice.value}
                onSelect={() => {
                  if (!current && !change.isPending) change.mutate(choice);
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body outline-none data-highlighted:bg-sunken',
                  current ? 'text-ink-faint' : 'text-ink',
                )}
              >
                <Check aria-hidden className={cn('size-3.5', current ? 'text-accent' : 'invisible')} />
                {choice.label}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
